// Lightweight synthesized sound effects using the Web Audio API.
// No external assets required — every sound is generated on demand from
// short envelope-shaped buffers.

let ctx: AudioContext | null = null;
let muted = false;

function getCtx(): AudioContext | null {
  if (typeof window === 'undefined') return null;
  if (!ctx) {
    const w = window as unknown as {
      AudioContext?: typeof AudioContext;
      webkitAudioContext?: typeof AudioContext;
    };
    const AC = w.AudioContext ?? w.webkitAudioContext;
    if (!AC) return null;
    try {
      ctx = new AC();
    } catch {
      ctx = null;
    }
  }
  if (ctx && ctx.state === 'suspended') {
    ctx.resume().catch(() => {});
  }
  return ctx;
}

export function setMuted(value: boolean): void {
  muted = value;
}

export function isMuted(): boolean {
  return muted;
}

function noiseBurst(durationMs: number, decay: number, gain: number): void {
  if (muted) return;
  const c = getCtx();
  if (!c) return;
  const len = Math.max(1, Math.floor((c.sampleRate * durationMs) / 1000));
  const buf = c.createBuffer(1, len, c.sampleRate);
  const data = buf.getChannelData(0);
  for (let i = 0; i < len; i++) {
    const t = i / c.sampleRate;
    data[i] = (Math.random() * 2 - 1) * Math.exp(-t * decay);
  }
  const src = c.createBufferSource();
  src.buffer = buf;
  const g = c.createGain();
  g.gain.value = gain;
  src.connect(g).connect(c.destination);
  src.start();
}

function tone(
  freq: number,
  durationMs: number,
  type: OscillatorType = 'sine',
  gainValue = 0.18,
): void {
  if (muted) return;
  const c = getCtx();
  if (!c) return;
  const osc = c.createOscillator();
  const g = c.createGain();
  osc.type = type;
  osc.frequency.value = freq;
  const now = c.currentTime;
  g.gain.setValueAtTime(0, now);
  g.gain.linearRampToValueAtTime(gainValue, now + 0.005);
  g.gain.exponentialRampToValueAtTime(0.0001, now + durationMs / 1000);
  osc.connect(g).connect(c.destination);
  osc.start(now);
  osc.stop(now + durationMs / 1000 + 0.02);
}

function chord(freqs: number[], durationMs: number, gainValue = 0.1): void {
  for (const f of freqs) tone(f, durationMs, 'triangle', gainValue);
}

export const sfx = {
  // crisp click when a card lands on the felt
  cardPlay(): void {
    noiseBurst(120, 28, 0.45);
    tone(420, 80, 'square', 0.06);
  },
  // softer sweep for pass
  pass(): void {
    tone(360, 90, 'sine', 0.08);
    tone(260, 130, 'sine', 0.06);
  },
  // rapid shuffle sweep when cards are being dealt
  deal(): void {
    noiseBurst(260, 12, 0.3);
  },
  // dealing one card during the deal animation
  dealTick(): void {
    noiseBurst(45, 60, 0.18);
  },
  // gentle chime when it's the player's turn
  yourTurn(): void {
    tone(880, 180, 'sine', 0.16);
    setTimeout(() => tone(1175, 220, 'sine', 0.12), 90);
  },
  // round complete fanfare
  roundEnd(): void {
    chord([523, 659, 784], 320, 0.1);
  },
  // match complete bigger fanfare
  matchEnd(): void {
    chord([523, 659, 784, 988], 600, 0.12);
  },
  // error / invalid action
  error(): void {
    tone(180, 220, 'sawtooth', 0.18);
  },
  // triumphant victory fanfare — brassy ascending arpeggio into a held chord
  fanfare(): void {
    if (muted) return;
    const arp = [523.25, 659.25, 783.99, 1046.5]; // C5 E5 G5 C6
    arp.forEach((f, i) => {
      setTimeout(() => {
        tone(f, 200, 'square', 0.1);
        tone(f / 2, 200, 'triangle', 0.06); // octave-below body
      }, i * 120);
    });
    // final sustained major chord with a high sparkle on top
    setTimeout(() => {
      chord([523.25, 659.25, 783.99, 1046.5], 800, 0.1);
      tone(1567.98, 600, 'triangle', 0.06); // G6 shimmer
    }, arp.length * 120);
  },
};
